/**
 * GekkoVet API Client
 * Based on the authentication patterns from gekkovet-frontend-customer
 */

import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { Disease, DiseaseStub, Source, ApiError, PagedResponse } from '../types/api';
import { getAWSConfig } from '../aws.config';

export class GekkoApiError extends Error {
  statusCode: number;
  error: string;

  constructor(statusCode: number, error: string, message: string) {
    super(message);
    this.statusCode = statusCode;
    this.error = error;
    this.name = 'GekkoApiError';
  }
}

export class GekkoApi {
  private baseUrl: string;

  constructor() {
    const config = getAWSConfig();
    if (!config.apiEndpoint) {
      throw new Error('AWS_API_ENDPOINT is not configured');
    }
    this.baseUrl = config.apiEndpoint;
  }

  /**
   * Get authentication token from AWS Cognito session
   */
  private async getAuthToken(): Promise<string> {
    try {
      // Check if user is authenticated
      await getCurrentUser();
      
      // Get the current session
      const session = await fetchAuthSession();

      // Use access token for API Gateway Cognito authorizer
      const token = session.tokens?.accessToken?.toString();

      if (!token) {
        throw new Error('No access token available');
      }

      console.log('GekkoAPI using access token for authentication');
      return token;
    } catch (error) {
      console.error('Failed to get auth token:', error);
      throw new Error('Authentication required');
    }
  }

  /**
   * Make authenticated request to the API
   */
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    try {
      const token = await this.getAuthToken();
      
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers,
        },
      });

      if (!response.ok) {
        let errorMessage = 'Request failed';
        let errorType = 'UNKNOWN_ERROR';
        
        try {
          const errorData = await response.json() as ApiError;
          errorMessage = errorData.message || errorMessage;
          errorType = errorData.error || errorType;
        } catch {
          // If error response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }

        throw new GekkoApiError(response.status, errorType, errorMessage);
      }

      const data = await response.json();
      return data as T;
    } catch (error) {
      if (error instanceof GekkoApiError) {
        throw error;
      }
      
      // Handle network errors or other issues
      console.error('API request failed:', error);
      throw new GekkoApiError(0, 'NETWORK_ERROR', 'Network request failed');
    }
  }

  /**
   * Get detailed information for a specific disease
   */
  async getDisease(diseaseId: number): Promise<Disease> {
    const response = await this.request<Disease>(`/diseases/${diseaseId}`);
    return response;
  }

  /**
   * Get list of diseases for a specific animal type (with pagination)
   */
  async getDiseases(animalTypeId: number, page = 1): Promise<PagedResponse<DiseaseStub>> {
    const params = new URLSearchParams({ 
      animal_type_id: animalTypeId.toString(),
      page: page.toString()
    });
    const response = await this.request<PagedResponse<DiseaseStub>>(`/diseases?${params}`);
    return response;
  }

  /**
   * Create attachment record and get pre-signed S3 upload URL
   */
  async createAttachment(filename: string, mimeType: string): Promise<{
    id: string;
    filename: string;
    mime_type: string;
    size?: number;
    s3_key: string;
    status: string;
    created_at: string;
    updated_at: string;
    upload_url: string;
  }> {
    const response = await this.request<{
      id: string;
      filename: string;
      mime_type: string;
      size?: number;
      s3_key: string;
      status: string;
      created_at: string;
      updated_at: string;
      upload_url: string;
    }>('/cases/attachments', {
      method: 'POST',
      body: JSON.stringify({
        filename,
        mime_type: mimeType,
      }),
    });
    return response;
  }

  /**
   * Get source information by ID
   * Note: This endpoint might not exist yet in gekko-api, 
   * but we're preparing for when it's available
   */
  async getSource(sourceId: number): Promise<Source> {
    try {
      const response = await this.request<Source>(`/sources/${sourceId}`);
      return response;
    } catch (error) {
      // If the source doesn't exist, return an error placeholder
      if (error instanceof GekkoApiError && error.statusCode === 404) {
        return {
          id: sourceId,
          name: `Failed to load source information (id: ${sourceId})`
        };
      }
      throw error;
    }
  }

}

// Export singleton instance
export const gekkoApi = new GekkoApi();
